import React, { createContext, useContext, useMemo, useState, ReactNode } from "react";

// ----- Types from System Settings -----
export type GlobalFieldKey = "dailyLeadVolume" | "leadQuality" | "baseConversionRate" | "cycleTimeImpact";
export interface GlobalValues {
  dailyLeadVolume: number; // cases/day
  leadQuality: number; // 0-100
  baseConversionRate: number; // 0-100
  cycleTimeImpact: number; // placeholder for future modeling
}

export interface ProcessBase {
  name: string;
  capacityPerPerson: number; // hours/day
  simpleTime: number; // hours per simple case
  complexTime: number; // hours per complex case
  complexityPct: number; // % complex 0-100
  baseReworkPct: number; // 0-100
  experienceImpact: number; // max time reduction at 100% experience (0-100)
  automationImpact: number; // max time reduction at 100% automation (0-100)
  maturityImpact: number; // max rework reduction at 100% maturity (0-100)
}

export interface ProcessFactors {
  staff: number; // 1..5
  experience: number; // 0..100
  automation: number; // 0..100
  maturity: number; // 0..100
}

export interface ProcessDerived {
  avgBaseTime: number; // hours/case before reductions
  effectiveTime: number; // hours/case after reductions
  effectiveReworkPct: number; // 0..100
  dailyCapacityHours: number; // hours/day
  throughputPerDay: number; // cases/day
  demandPerDay: number; // incoming cases/day (incl. conversion not applied yet)
  effectiveDemandPerDay: number; // demand including rework loops
  utilizationPct: number; // 0..100
  queueIncreasePerDay: number; // cases/day that queue up
}

export interface ProcessState {
  base: ProcessBase;
  factors: ProcessFactors;
  derived: ProcessDerived;
}

export interface SimulatorState {
  global: GlobalValues;
  processes: ProcessState[];
  bottleneckIndex: number; // index of max utilization
  minLineThroughputPerDay: number; // constrained by bottleneck
  policiesPerDay: number; // output (after conversion)
  avgCycleTimeDays: number; // naive cycle time across chain
}

interface SimulatorContextValue extends SimulatorState {
  setGlobal: (patch: Partial<GlobalValues>) => void;
  updateProcessBase: (idx: number, patch: Partial<ProcessBase>) => void;
  updateProcessFactors: (idx: number, patch: Partial<ProcessFactors>) => void;
}

const DEFAULT_GLOBAL: GlobalValues = {
  dailyLeadVolume: 13,
  leadQuality: 75,
  baseConversionRate: 50,
  cycleTimeImpact: 8,
};

const DEFAULT_PROCESSES: ProcessBase[] = [
  { name: "Contact Management", capacityPerPerson: 8, simpleTime: 0.8, complexTime: 1.5, complexityPct: 60, baseReworkPct: 25, experienceImpact: 50, automationImpact: 50, maturityImpact: 30 },
  { name: "Pipeline Management", capacityPerPerson: 8, simpleTime: 0.25, complexTime: 0.55, complexityPct: 50, baseReworkPct: 30, experienceImpact: 50, automationImpact: 50, maturityImpact: 30 },
  { name: "Document Review", capacityPerPerson: 8, simpleTime: 0.3, complexTime: 0.65, complexityPct: 70, baseReworkPct: 40, experienceImpact: 50, automationImpact: 50, maturityImpact: 30 },
  { name: "Quoting", capacityPerPerson: 8, simpleTime: 0.6, complexTime: 1.8, complexityPct: 60, baseReworkPct: 35, experienceImpact: 50, automationImpact: 50, maturityImpact: 30 },
  { name: "Compliance", capacityPerPerson: 8, simpleTime: 0.3, complexTime: 1.0, complexityPct: 40, baseReworkPct: 20, experienceImpact: 50, automationImpact: 50, maturityImpact: 30 },
];

const DEFAULT_FACTORS: ProcessFactors[] = [
  { staff: 1, experience: 60, automation: 0, maturity: 0 }, // Contact
  { staff: 1, experience: 60, automation: 0, maturity: 0 }, // Pipeline
  { staff: 1, experience: 75, automation: 0, maturity: 0 }, // Document
  { staff: 1, experience: 70, automation: 0, maturity: 0 }, // Quoting
  { staff: 1, experience: 80, automation: 0, maturity: 0 }, // Compliance
];

function computeDerived(base: ProcessBase, factors: ProcessFactors, global: GlobalValues): ProcessDerived {
  const complexFrac = Math.min(1, Math.max(0, base.complexityPct / 100));
  const avgBaseTime = base.simpleTime * (1 - complexFrac) + base.complexTime * complexFrac; // hours/case

  // Impacts are the max improvement at 100% factor level
  const expReduction = (base.experienceImpact / 100) * (factors.experience / 100);
  const autoReduction = (base.automationImpact / 100) * (factors.automation / 100);
  const maturityReduction = (base.maturityImpact / 100) * (factors.maturity / 100);

  // Formulas provided
  const effectiveTime = avgBaseTime * (1 - expReduction) * (1 - autoReduction);
  const effectiveReworkPct = base.baseReworkPct * (1 - maturityReduction); // still in 0..100

  const dailyCapacityHours = base.capacityPerPerson * Math.max(0, factors.staff);
  const throughputPerDay = effectiveTime > 0 ? dailyCapacityHours / effectiveTime : 0;

  // Demand per process uses daily lead volume. Optionally modulate by lead quality in the future.
  const demandPerDay = Math.max(0, global.dailyLeadVolume);
  const effectiveDemandPerDay = demandPerDay * (1 + effectiveReworkPct / 100);

  const requiredHours = effectiveDemandPerDay * effectiveTime;
  const utilizationPct = dailyCapacityHours > 0 ? Math.min(100, (requiredHours / dailyCapacityHours) * 100) : 0;
  const queueIncreasePerDay = Math.max(0, effectiveDemandPerDay - throughputPerDay);

  return {
    avgBaseTime,
    effectiveTime,
    effectiveReworkPct,
    dailyCapacityHours,
    throughputPerDay,
    demandPerDay,
    effectiveDemandPerDay,
    utilizationPct,
    queueIncreasePerDay,
  };
}

const SimulatorContext = createContext<SimulatorContextValue | null>(null);

export function SimulatorProvider({ children }: { children: ReactNode }) {
  const [global, setGlobalState] = useState<GlobalValues>(DEFAULT_GLOBAL);
  const [bases, setBases] = useState<ProcessBase[]>(DEFAULT_PROCESSES);
  const [factors, setFactors] = useState<ProcessFactors[]>(DEFAULT_FACTORS);
  const inputsZero =
    global.dailyLeadVolume === 0 &&
    global.leadQuality === 0 &&
    global.baseConversionRate === 0 &&
    global.cycleTimeImpact === 0;

  const processes = useMemo<ProcessState[]>(() => {
    return bases.map((b, i) => {
      const f = factors[i];
      const derived = computeDerived(b, f, global);
      return { base: b, factors: f, derived };
    });
  }, [bases, factors, global]);

  const bottleneckIndex = useMemo(() => {
    if (processes.length === 0) return -1;
    let idx = 0;
    let maxU = -Infinity;
    processes.forEach((p, i) => {
      if (p.derived.utilizationPct > maxU) {
        maxU = p.derived.utilizationPct;
        idx = i;
      }
    });
    return idx;
  }, [processes]);

  const minLineThroughputPerDay = useMemo(() => {
    if (processes.length === 0) return 0;
    return Math.min(...processes.map((p) => p.derived.throughputPerDay));
  }, [processes]);

  const policiesPerDay = useMemo(() => {
    const conv = Math.max(0, Math.min(100, global.baseConversionRate)) / 100;
    const capacityLimited = Math.min(global.dailyLeadVolume, minLineThroughputPerDay);
    return capacityLimited * conv;
  }, [global.baseConversionRate, global.dailyLeadVolume, minLineThroughputPerDay]);

  const avgCycleTimeDays = useMemo(() => {
    if (inputsZero) return 0;
    // Naive: sum effective times (+ extra due to rework) across processes; convert hours to days (8h/day)
    const hours = processes.reduce((sum, p) => sum + p.derived.effectiveTime * (1 + p.derived.effectiveReworkPct / 100), 0);
    return hours / 8; // 8 work-hours per day
  }, [inputsZero, processes]);

  const value: SimulatorContextValue = {
    global,
    processes,
    bottleneckIndex,
    minLineThroughputPerDay,
    policiesPerDay,
    avgCycleTimeDays,
    setGlobal: (patch) => setGlobalState((g) => ({ ...g, ...patch })),
    updateProcessBase: (idx, patch) =>
      setBases((arr) => arr.map((b, i) => (i === idx ? { ...b, ...patch } : b))),
    updateProcessFactors: (idx, patch) =>
      setFactors((arr) => arr.map((f, i) => (i === idx ? { ...f, ...patch } : f))),
  };

  return <SimulatorContext.Provider value={value}>{children}</SimulatorContext.Provider>;
}

export function useSimulator(): SimulatorContextValue {
  const ctx = useContext(SimulatorContext);
  if (!ctx) throw new Error("useSimulator must be used within SimulatorProvider");
  return ctx;
}
